import { useWidget } from "../../../../providers/widget"
import { ExploreWidgetConfig } from "../../config"
import { useExplorer } from "../../providers"
import LocationFilter from "./LocationFilter"
import ZoneFilter from "./ZoneFilter"
import DateFilter from "./DateFilter"
import GuestFilter from "./GuestFilter"
import AdditionalFilters from "./AdditionalFilters"
import ProFilterBar from "./pro/ProFilterBar"
import { useFilterTotals, useSearchSubmit, SecondaryRow, isHeroBar } from "./searchShared"

/**
 * Variant router (#853): `searchbar.variant` picks the search UI. 'pro' (the
 * schema default — every widget gets the Searchbar Pro look unless it opts
 * out) renders the SBP port; 'legacy' renders the original filter bar.
 */
/**
 * `withSecondaryRow` (#1251): whether this bar owns the results header.
 * False on the mobile results sheet, where the header is mounted once in the
 * sheet head instead — see ExplorerContent.
 */
export default function FilterBar({ withSecondaryRow = true }: { withSecondaryRow?: boolean } = {}) {
  const { options } = useWidget<ExploreWidgetConfig>()
  if (options.searchbar.variant === 'pro') return <ProFilterBar withSecondaryRow={withSecondaryRow} />
  return <LegacyFilterBar withSecondaryRow={withSecondaryRow} />
}

function LegacyFilterBar({ withSecondaryRow }: { withSecondaryRow: boolean }) {
  const { options } = useWidget<ExploreWidgetConfig>()
  const { state } = useExplorer()
  const totalItems = useFilterTotals()
  const submitSearch = useSearchSubmit()

  const handleSearchClick = (e: React.MouseEvent) => {
    e.preventDefault()
    submitSearch()
  }

  // Which primary inputs to show, and in what order (WP: primary_filter_fields).
  // Falls back to the full default set for older configs that predate this field.
  const primaryFields = options.primaryFilters?.length
    ? options.primaryFilters
    : ['where', 'date', 'guests', 'additional-filters']

  const renderPrimaryField = (field: string) => {
    switch (field) {
      case 'where': return options.whereFieldSource === 'zone'
        ? <ZoneFilter key={`zone-${state.resetKey}`} />
        : <LocationFilter key={`loc-${state.resetKey}`} />
      case 'date': return <DateFilter key={`date-${state.resetKey}`} />
      case 'guests': return <GuestFilter key={`guest-${state.resetKey}`} />
      case 'additional-filters': return <AdditionalFilters key={`addl-${state.resetKey}`} />
      default: return null
    }
  }

  return (
    <div className="hfse-filter-bar">
      {/* Primary filters */}
      <form method="get" noValidate className="hfse-form hfse-filters" onSubmit={e => e.preventDefault()}>
        <div className="hfse-form-primary-fields">
          {primaryFields.map(renderPrimaryField)}
          <div className="hfse-form-search-btn-container">
            <button
              type="submit"
              className="hk-button hfse-form-search-btn bg-accent"
              onClick={handleSearchClick}
            >
              {options.submitBtnText || 'Search'}
            </button>
          </div>
        </div>
      </form>

      {/* Hero (#934): no results on the page → no results chrome, same gate
          as the pro variant.

          #1251: on the mobile results sheet the results header belongs to the
          sheet HEAD, not to the bar, so the parent renders it there and passes
          withSecondaryRow={false}. It used to mount here as well and be hidden
          with display:none — which kept a second SortDropdown running its
          canvas measurement against an unrendered element, and a second
          aria-live count region re-rendering on every totals change. */}
      {!isHeroBar(options) && withSecondaryRow && <SecondaryRow totalItems={totalItems} />}
    </div>
  )
}
